import { useMemo } from 'react';

import {
  CATALOG_QUERY,
  type CatalogQuery,
  type CatalogQueryVariables,
  type CompositionV3Ent,
  type CompositionV3Req,
} from '@revelio/data-access';

import { useIsTaxonomyResolved } from '../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { isCompositionV3CustomSkillTaxonomyReady } from '../../../filters/utils/composition-v3/is-composition-v3-custom-skill-taxonomy-ready';
import { getMonthRefsFromV3Filters } from '../../../filters/utils/get-v3-month-refs';
import {
  areCorrectionPreferencesReady,
  isCompositionV3QueryVariablesReady,
  resolveCompositionV3RequestFilters,
  useCompositionV3Setup,
} from '../composition/CompositionRawDataProvider';
import { DataProviderQueryStatus, type DataProviderResult } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import {
  areStringArraysEqual,
  useStableReference,
} from '../utils/use-stable-reference';
import {
  type DiversityCatalogEntity,
  isDiversityCatalogSupported,
  parseDiversityCatalog,
  toDiversityCatalogRequest,
} from './diversity-catalog';

type UseDiversityCatalogDataOptions = {
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV3Ent[];
  pause?: boolean;
};

export type DiversityCatalogDataResult = DataProviderResult<
  DiversityCatalogEntity[]
> & { isSupported: boolean };

export const useDiversityCatalogData = (
  primaryDimension: PrimaryFilterId,
  {
    isTimeseries = false,
    entitiesOverride,
    pause = false,
  }: UseDiversityCatalogDataOptions = {}
): DiversityCatalogDataResult => {
  const {
    revelioPageId,
    config,
    activeFilterMap,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographySelectionListIds,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = useCompositionV3Setup();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const isTaxonomyResolved = useIsTaxonomyResolved();

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: !isTimeseries,
    geographySelectionListIds,
    customSkillTree,
    compositionEntityScopes: entitiesOverride,
    activeFilterMap,
  });
  const compositionRequest: CompositionV3Req = {
    ...baseVariables,
    filters: resolveCompositionV3RequestFilters({
      filters: baseVariables.filters,
      isTimeseries,
      revelioPageId,
      dateRangeDefault: config.dateRangeDefault,
      availableDateRange,
    }),
  };

  const isSupported = isDiversityCatalogSupported(compositionRequest.entities);
  const isReady =
    isTaxonomyResolved &&
    !isPreferencesInitialLoading &&
    isCompositionV3QueryVariablesReady(compositionRequest) &&
    areCorrectionPreferencesReady(config.secondaryFilters, activeFilterMap) &&
    isCompositionV3CustomSkillTaxonomyReady({
      effectiveFilters,
      customSkillTree,
      isCustomSkillLevelMapReady,
      isCustomSkillTaxonomyPending,
    });

  const [{ data, status, error }] = useUrqlQueryWithStatus<
    CatalogQuery,
    CatalogQueryVariables
  >({
    query: CATALOG_QUERY,
    variables: { req: toDiversityCatalogRequest(compositionRequest) },
    pause: pause || !isSupported || !isReady,
  });

  const parsed = useMemo(() => {
    if (!data) {
      return { entities: undefined, error: undefined };
    }
    try {
      return {
        entities: parseDiversityCatalog(data.catalog),
        error: undefined,
      };
    } catch (parseError) {
      return {
        entities: undefined,
        error:
          parseError instanceof Error
            ? parseError
            : new Error(String(parseError)),
      };
    }
  }, [data]);

  const monthRefs = useStableReference(
    getMonthRefsFromV3Filters(compositionRequest.filters),
    areStringArraysEqual
  );

  if (!isSupported) {
    return {
      data: undefined,
      status: DataProviderQueryStatus.Idle,
      monthRefs,
      isSupported,
    };
  }

  return {
    data: parsed.entities,
    status: parsed.error ? DataProviderQueryStatus.Error : status,
    error: parsed.error ?? error,
    monthRefs,
    isSupported,
  };
};
